/**
 * Partner session.
 *
 * Three states, and the app routes on all three:
 *   signed out          → OTP screen
 *   signed in, no vendor → registration
 *   signed in, vendor    → the app
 *
 * The middle state is real and common: a printer verifies their number, gets
 * distracted, and comes back an hour later. Dropping them at the OTP screen
 * again — or worse, into an app with no profile — is how that person never
 * finishes registering.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  ApiError,
  storeToken,
  storedToken,
  type MeResponse,
  type VendorProfile,
} from './api';

interface Session {
  loading: boolean;
  signedIn: boolean;
  registered: boolean;
  phone: string | null;
  vendor: VendorProfile | null;
  /** This login: name, owner or staff, and when its photo last changed. */
  login: { name: string | null; role: string; photoAt: string | null } | null;
  refresh: () => Promise<void>;
  signIn: (token: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<MeResponse | null>(null);

  const refresh = useCallback(async () => {
    if (!storedToken()) {
      setMe(null);
      setLoading(false);
      return;
    }

    try {
      setMe(await api.get<MeResponse>('/v1/partner/me'));
    } catch (err) {
      // Only a dead session clears state. A network blip must not sign a
      // vendor out mid-job — they are frequently on one bar of signal.
      if (err instanceof ApiError && err.status === 401) {
        storeToken(null);
        setMe(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (token: string) => {
      storeToken(token);
      setLoading(true);
      await refresh();
    },
    [refresh],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/v1/partner/logout');
    } finally {
      storeToken(null);
      setMe(null);
    }
  }, []);

  const value = useMemo<Session>(
    () => ({
      loading,
      signedIn: me !== null,
      registered: me?.registered === true,
      phone: me?.phone ?? null,
      vendor: me?.vendor ?? null,
      login: me?.login ?? null,
      refresh,
      signIn,
      signOut,
    }),
    [loading, me, refresh, signIn, signOut],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}
